import React, { useCallback, useState } from 'react';

import { newReportMenuButton } from '@aline/common';
import { Menu } from '@mui/material';
import { ChartColumnBig, Sparkles } from 'lucide-react';
import { NewReportDialog, OptionsMenuItem, PrimaryPlusIconButton } from 'src/components';
import { queuePromptDraft, requestPromptDraft } from 'src/components/ai/chat/chat-state';
import { useAccessGuard } from 'src/components/auth';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai';
import { Apollo } from 'src/gql/generated';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

/** What the reports page puts in chat when the user starts a report with AI. */
export function createNewReportPrompt(): string {
  return 'Create a new report **[attach files to build from, or describe the contract types and the goal]**.';
}

export function NewReportMenu(): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [newReportDialogOpen, setNewReportDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { openDrawer } = useGlobalDrawer();
  const openBlankDialog = useAccessGuard({
    callback: () => {
      setMenuOpen(false);
      setNewReportDialogOpen(true);
    },
    minRole: Apollo.OrgRole.Basic,
  });
  const createWithAi = useAccessGuard({
    callback: () => {
      setMenuOpen(false);
      const prompt = createNewReportPrompt();
      const isLeavingExistingThread = Boolean(aiThreadIdVar());
      aiThreadIdVar(null);
      openDrawer();
      if (isLeavingExistingThread) queuePromptDraft(prompt);
      else requestPromptDraft(prompt);
    },
    minRole: Apollo.OrgRole.Basic,
  });
  const handleCloseNewReportDialog = useCallback(() => {
    setNewReportDialogOpen(false);
  }, []);

  return (
    <>
      <PrimaryPlusIconButton
        ref={setAnchorEl}
        data-cy={newReportMenuButton}
        tooltip="New Report"
        onClick={() => setMenuOpen(current => !current)}
      />
      <Menu
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={() => setMenuOpen(false)}
      >
        <OptionsMenuItem
          clickHandler={openBlankDialog}
          icon={<ChartColumnBig size={16} />}
          text="Create blank report"
        />
        <OptionsMenuItem
          clickHandler={createWithAi}
          icon={<Sparkles size={16} />}
          text="Create report with AI"
        />
      </Menu>
      <NewReportDialog handleClose={handleCloseNewReportDialog} open={newReportDialogOpen} />
    </>
  );
}
